import { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ClockIcon,
  RocketLaunchIcon,
  InformationCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ContentItem } from '../../types/content';
import MobileContentItemSheet from './MobileContentItemSheet';

interface ContentSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: ContentItem[];
}

const PAGE_DB_MAP: Record<string, string> = {
  'hero-slider': 'home',
  'gallery': 'history',
};

const PAGE_SECTIONS: Record<string, { title: string; icon: React.ComponentType<{ className?: string }> }[]> = {
  home: [
    { title: 'About Section', icon: InformationCircleIcon },
    { title: 'Tournament Legacy', icon: RocketLaunchIcon },
  ],
  about: [
    { title: 'Page Header', icon: InformationCircleIcon },
    { title: 'Mission', icon: DocumentTextIcon },
    { title: 'Committee', icon: RocketLaunchIcon },
    { title: 'Testimonials', icon: RocketLaunchIcon },
  ],
  'hero-slider': [
    { title: 'Hero Slider', icon: RectangleStackIcon },
  ],
  registration: [
    { title: 'Registration Content', icon: DocumentTextIcon },
  ],
  history: [
    { title: 'Page Header', icon: ClockIcon },
    { title: 'Press', icon: DocumentTextIcon },
    { title: 'Gallery', icon: PhotoIcon },
  ],
  gallery: [
    { title: 'Gallery', icon: PhotoIcon },
  ],
};


function parseContent(content: any): any {
  if (typeof content === 'string') {
    try { return JSON.parse(content); } catch { return content; }
  }
  return content;
}

function renderContentPreview(content: any): string {
  const parsed = parseContent(content);
  if (typeof parsed === 'string') return parsed.length > 100 ? parsed.slice(0, 100) + '...' : parsed;
  if (Array.isArray(parsed)) return `${parsed.length} item${parsed.length !== 1 ? 's' : ''}`;
  if (typeof parsed === 'object' && parsed !== null) return JSON.stringify(parsed).slice(0, 80) + '...';
  return String(parsed ?? '');
}

interface Props {
  page: string;
}

export default function MobileContentSectionManager({ page }: Props) {
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ContentItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [page]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const dbPage = PAGE_DB_MAP[page] ?? page;
      const initialSections = PAGE_SECTIONS[page] || [];

      for (const section of initialSections) {
        const { data: existing } = await supabase
          .from('content_sections')
          .select('id')
          .eq('page', dbPage)
          .eq('title', section.title)
          .maybeSingle();

        if (!existing) {
          await supabase.from('content_sections').insert([{
            page: dbPage,
            title: section.title,
            icon: section.icon.name || 'RocketLaunchIcon',
          }]);
        }
      }

      const sectionTitles = initialSections.map(s => s.title);
      const { data: dbSections, error } = await supabase
        .from('content_sections')
        .select('*, content_items(*)')
        .eq('page', dbPage)
        .in('title', sectionTitles.length > 0 ? sectionTitles : ['__none__']);

      if (error) throw error;

      const processed: ContentSection[] = (dbSections || []).map(dbSection => {
        const match = initialSections.find(s => s.title === dbSection.title);
        return {
          id: dbSection.id,
          title: dbSection.title,
          icon: match?.icon || RocketLaunchIcon,
          items: (dbSection.content_items || []).map((item: any) => ({
            ...item,
            lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0],
          })),
        };
      });

      setSections(processed);
      if (!expandedSection && processed.length > 0) {
        setExpandedSection(processed[0].id);
      }
    } catch {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (sectionId: string) => {
    setSelectedItem(null);
    setSelectedSectionId(sectionId);
    setSheetOpen(true);
  };

  const handleEdit = (sectionId: string, item: ContentItem) => {
    const editItem = {
      ...item,
      content: typeof item.content === 'string' ? item.content : JSON.stringify(item.content),
    };
    setSelectedItem(editItem);
    setSelectedSectionId(sectionId);
    setSheetOpen(true);
  };

  const handleSave = async (item: ContentItem) => {
    try {
      let parsedContent: any;
      try { parsedContent = JSON.parse(item.content); }
      catch { parsedContent = item.content; }

      const itemData = {
        title: item.title,
        type: item.type,
        content: parsedContent,
        status: item.status,
        section_id: selectedSectionId,
      };

      if (selectedItem?.id) {
        const { error } = await supabase.from('content_items').update(itemData).eq('id', selectedItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('content_items').insert([itemData]);
        if (error) throw error;
      }

      await fetchContent();
      setSheetOpen(false);
      toast.success('Content saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save content');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      const { error } = await supabase.from('content_items').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      await fetchContent();
      setDeleteTarget(null);
      toast.success('Content deleted');
    } catch {
      toast.error('Failed to delete content');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-3">
      {sections.map(section => (
        <div key={section.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
            className="w-full flex items-center justify-between px-4 py-4 text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                <section.icon className="w-4.5 h-4.5 text-golf-orange" />
              </div>
              <div>
                <p className="text-sm font-semibold text-golf-gray">{section.title}</p>
                <p className="text-xs text-gray-400">{section.items.length} item{section.items.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={e => { e.stopPropagation(); handleAdd(section.id); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-golf-orange/10 active:bg-golf-orange/20 transition-colors"
              >
                <PlusIcon className="w-4 h-4 text-golf-orange" />
              </button>
              {expandedSection === section.id
                ? <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                : <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              }
            </div>
          </button>

          {expandedSection === section.id && (
            <div className="border-t border-gray-100 divide-y divide-gray-50">
              {section.items.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-gray-400">No items yet</p>
                  <button
                    onClick={() => handleAdd(section.id)}
                    className="mt-2 text-xs text-golf-orange font-medium"
                  >
                    Add the first item
                  </button>
                </div>
              ) : (
                section.items.map(item => (
                  <div key={item.id} className="px-4 py-3.5 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-golf-gray truncate">{item.title}</p>
                        <span className="text-xs text-gray-400 capitalize">{item.type}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                          item.status === 'Published'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.status === 'Published'
                            ? <CheckCircleIcon className="w-3 h-3" />
                            : <ClockIcon className="w-3 h-3" />
                          }
                          {item.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{renderContentPreview(item.content)}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                      <button
                        onClick={() => handleEdit(section.id, item)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 active:bg-blue-100 transition-colors"
                      >
                        <PencilIcon className="w-3.5 h-3.5 text-blue-600" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(item)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 active:bg-red-100 transition-colors"
                      >
                        <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}

      <MobileContentItemSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSave={handleSave}
        item={selectedItem}
        sectionId={selectedSectionId}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative w-full bg-white rounded-t-3xl px-5 py-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold text-golf-gray">Delete Content?</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete <span className="font-semibold text-golf-gray">"{deleteTarget.title}"</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:bg-red-600 disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
